import React, { useRef, useState } from 'react';
import { Box, Collapse, Skeleton, Typography } from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import type { PublicAgent } from '../../../share-public/sharePublicApi';
import { focusedPhotoStyle } from '../../../share-public/components/AgentAvatar';

// The consumer site's palette (share-public/sharePublic.css) — the preview stays dark in either app theme.
const GOLD = '#e8bd7c';
const TEXT = '#eef2f7';
const MUTED = '#9aa8bb';

const initials = (name: string | null) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');

const firstName = (name: string | null) => (name || '').trim().split(/\s+/)[0] || 'agent';

function PreviewAvatar({ agent }: { agent: PublicAgent }) {
  const [failed, setFailed] = useState(false);
  return (
    <Box sx={{
      position: 'relative', width: 52, height: 52, flex: 'none', borderRadius: '50%', overflow: 'hidden',
      display: 'grid', placeItems: 'center', border: `1.5px solid ${GOLD}99`,
      background: 'linear-gradient(160deg, #22324a, #121c2c)', color: GOLD, fontWeight: 700, fontSize: 20,
    }}>
      {agent.photo && !failed ? (
        <Box component="img" src={agent.photo} alt="" onError={() => setFailed(true)}
          sx={agent.photoFocus ? undefined : { width: '100%', height: '100%', objectFit: 'cover' }}
          style={agent.photoFocus ? focusedPhotoStyle(agent.photoFocus) : undefined} />
      ) : initials(agent.name)}
    </Box>
  );
}

interface Props {
  agent: PublicAgent | null;
  error?: string | null;
  /** The line under the name — the agent's title, their tagline, or null for none. */
  subtitle: string | null;
}

/**
 * A static copy of the consumer site's agent card (share-public `AgentCard`), so the
 * agent sees what their visitors will see as they switch the title on and off or type a tagline.
 */
export function AgentCardPreview({ agent, error, subtitle }: Props) {
  // Keep the last line on screen while it collapses away.
  const lastSubtitle = useRef(subtitle);
  if (subtitle) lastSubtitle.current = subtitle;
  return (
    <Box
      aria-label="Preview of your agent card"
      sx={{
        position: 'relative', boxSizing: 'border-box', width: '100%', maxWidth: 360, mx: 'auto', p: 2, borderRadius: '18px',
        bgcolor: 'rgba(9, 18, 32, 0.96)', border: '1px solid rgba(255, 255, 255, 0.09)', color: TEXT,
        boxShadow: '0 14px 36px rgba(0, 0, 0, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
        // Display-only: nothing in the copy is clickable.
        pointerEvents: 'none', userSelect: 'none',
      }}
    >
      <Typography component="div" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, pb: 1.25 }}>
        Your agent
      </Typography>
      {error ? (
        <Typography variant="body2" sx={{ color: MUTED, py: 1 }}>{error}</Typography>
      ) : !agent ? (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Skeleton variant="circular" width={52} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="70%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
            <Skeleton width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <PreviewAvatar agent={agent} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: 16, color: TEXT }}>{agent.name || 'Huttons agent'}</Typography>
              <Collapse in={!!subtitle} timeout={220}>
                <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>{subtitle || lastSubtitle.current}</Typography>
              </Collapse>
            </Box>
          </Box>
          <Box sx={{ my: 1.5, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.04)', fontSize: 11, lineHeight: 1.6, color: MUTED }}>
            CEA Reg No. <Box component="b" sx={{ color: TEXT }}>{agent.ceaNumber}</Box><br />
            {agent.agencyName} · Licence <Box component="b" sx={{ color: TEXT }}>{agent.agencyLicenceNo}</Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, height: 36, borderRadius: 999,
              bgcolor: '#25d366', color: '#05311a', fontSize: 13, fontWeight: 700, minWidth: 0, px: 1.5, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              <WhatsAppIcon sx={{ fontSize: 17 }} /> WhatsApp {firstName(agent.name)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', height: 36, px: 1.75, borderRadius: 999,
              border: '1px solid rgba(255, 255, 255, 0.18)', fontSize: 13, fontWeight: 700 }}>
              ☏ Call
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
